import React, { useState, useEffect } from 'react';
import {
  Search,
  Filter,
  X,
  CheckCircle,
  Briefcase,
  Calendar,
  Code,
  SlidersHorizontal,
} from 'lucide-react';
import { getFirestore, collection, query, where, getDocs } from 'firebase/firestore';
import { firebaseApp } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';

interface CandidateFiltersProps {
  onFilterChange: (filters: any) => void;
  candidatesCount: number;
}

const CandidateFilters: React.FC<CandidateFiltersProps> = ({
  onFilterChange,
  candidatesCount,
}) => {
  const { user } = useAuth();
  const db = getFirestore(firebaseApp);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedJob, setSelectedJob] = useState('all');
  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
  const [experienceLevel, setExperienceLevel] = useState('all');
  const [verificationStatus, setVerificationStatus] = useState<'all' | boolean>('all');
  const [dateRange, setDateRange] = useState<'all' | '7days' | '30days' | 'custom'>('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [skillInput, setSkillInput] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);

  const [jobs, setJobs] = useState<any[]>([]);
  const [loadingJobs, setLoadingJobs] = useState(true);

  // Fetch HR's jobs
  useEffect(() => {
    if (!user) return;

    const fetchJobs = async () => {
      try {
        const jobsQuery = query(
          collection(db, 'hrJobs'),
          where('hrId', '==', user.id)
        );

        const snapshot = await getDocs(jobsQuery);
        const jobsList = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setJobs(jobsList);
      } catch (error) {
      } finally {
        setLoadingJobs(false);
      }
    };

    fetchJobs();
  }, [user, db]);

  // Update filters whenever any filter changes
  useEffect(() => {
    const filters: any = {
      searchTerm: searchTerm.trim(),
      jobId: selectedJob !== 'all' ? selectedJob : undefined,
      skills: selectedSkills.length > 0 ? selectedSkills : undefined,
      experienceLevel: experienceLevel !== 'all' ? experienceLevel : undefined,
      verificationStatus,
      dateRange: null,
    };

    // Handle date range
    if (dateRange === '7days') {
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 7);
      filters.dateRange = { start, end };
    } else if (dateRange === '30days') {
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 30);
      filters.dateRange = { start, end };
    } else if (dateRange === 'custom' && customStartDate && customEndDate) {
      filters.dateRange = {
        start: new Date(customStartDate),
        end: new Date(customEndDate),
      };
    }

    onFilterChange(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    searchTerm,
    selectedJob,
    selectedSkills,
    experienceLevel,
    verificationStatus,
    dateRange,
    customStartDate,
    customEndDate,
    // Intentionally NOT including onFilterChange to prevent infinite loop
  ]);

  const handleAddSkill = () => {
    if (skillInput.trim() && !selectedSkills.includes(skillInput.trim())) {
      setSelectedSkills([...selectedSkills, skillInput.trim()]);
      setSkillInput('');
    }
  };

  const handleRemoveSkill = (skill: string) => {
    setSelectedSkills(selectedSkills.filter((s) => s !== skill));
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedJob('all');
    setSelectedSkills([]);
    setExperienceLevel('all');
    setVerificationStatus('all');
    setDateRange('all');
    setCustomStartDate('');
    setCustomEndDate('');
    setSkillInput('');
  };

  const activeFiltersCount =
    (searchTerm ? 1 : 0) +
    (selectedJob !== 'all' ? 1 : 0) +
    (selectedSkills.length > 0 ? 1 : 0) +
    (experienceLevel !== 'all' ? 1 : 0) +
    (verificationStatus !== 'all' ? 1 : 0) +
    (dateRange !== 'all' ? 1 : 0);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-6 space-y-4 w-full overflow-hidden">
      {/* Header */}
      <div className="flex flex-col gap-3">
        {/* Title Row */}
        <div className="flex items-center gap-2">
          <Filter className="w-5 h-5 text-red-600 flex-shrink-0" />
          <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
          {activeFiltersCount > 0 && (
            <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full font-medium whitespace-nowrap flex-shrink-0">
              {activeFiltersCount} active
            </span>
          )}
        </div>

        {/* Buttons Row */}
        <div className="flex items-center gap-2 flex-wrap">
          {activeFiltersCount > 0 && (
            <button
              onClick={handleClearFilters}
              className="text-xs sm:text-sm text-gray-600 hover:text-red-600 transition-colors flex items-center whitespace-nowrap"
            >
              <X className="w-4 h-4 mr-1 flex-shrink-0" />
              Clear all
            </button>
          )}
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            className={`px-3 py-1.5 rounded-lg transition-colors flex items-center text-xs sm:text-sm whitespace-nowrap ${
              showAdvanced
                ? 'bg-red-100 text-red-700'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <SlidersHorizontal className="w-4 h-4 mr-1 flex-shrink-0" />
            {showAdvanced ? 'Hide' : 'Show'} Advanced
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2 flex-shrink-0" />
        <input
          type="text"
          placeholder="Search by name or email"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 pr-4 py-2.5 w-full border border-gray-300 rounded-lg focus:ring-red-500 focus:border-red-500 text-sm"
        />
      </div>

      {/* Job Filter */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
          <Briefcase className="w-4 h-4 mr-1" />
          Filter by Job Posting
        </label>
        <select
          value={selectedJob}
          onChange={(e) => setSelectedJob(e.target.value)}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-red-500 focus:border-red-500"
          disabled={loadingJobs}
        >
          <option value="all">All Jobs</option>
          {jobs.map((job) => (
            <option key={job.id} value={job.id}>
              {job.jobTitle}
            </option>
          ))}
        </select>
        {selectedJob !== 'all' && (
          <p className="text-xs text-gray-500 mt-1">
            Skills match % will be calculated based on this job's requirements
          </p>
        )}
      </div>

      {/* Skills Filter */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
          <Code className="w-4 h-4 mr-1" />
          Filter by Skills
        </label>
        <div className="flex gap-2 mb-2">
          <input
            type="text"
            placeholder="Type a skill"
            value={skillInput}
            onChange={(e) => setSkillInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddSkill();
              }
            }}
            className="flex-1 min-w-0 border border-gray-300 rounded-lg px-3 py-2 focus:ring-red-500 focus:border-red-500 text-sm"
          />
          <button
            onClick={handleAddSkill}
            className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium whitespace-nowrap flex-shrink-0"
          >
            Add
          </button>
        </div>
        {selectedSkills.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {selectedSkills.map((skill) => (
              <span
                key={skill}
                className="inline-flex items-center px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm"
              >
                {skill}
                <button
                  onClick={() => handleRemoveSkill(skill)}
                  className="ml-1.5 hover:text-red-900"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Advanced Filters */}
      {showAdvanced && (
        <div className="pt-4 border-t border-gray-200 space-y-4">
          {/* Experience Level */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Experience Level
            </label>
            <select
              value={experienceLevel}
              onChange={(e) => setExperienceLevel(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-red-500 focus:border-red-500"
            >
              <option value="all">All Levels</option>
              <option value="intern">Intern</option>
              <option value="junior">Junior (0-2 years)</option>
              <option value="mid">Mid-Level (2-5 years)</option>
              <option value="senior">Senior (5+ years)</option>
            </select>
          </div>

          {/* Verification Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
              <CheckCircle className="w-4 h-4 mr-1" />
              Verification Status
            </label>
            <select
              value={String(verificationStatus)}
              onChange={(e) => {
                const value = e.target.value;
                setVerificationStatus(
                  value === 'all' ? 'all' : value === 'true'
                );
              }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-red-500 focus:border-red-500"
            >
              <option value="all">All Candidates</option>
              <option value="true">Verified Only ✓</option>
              <option value="false">Not Verified</option>
            </select>
          </div>

          {/* Date Range */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
              <Calendar className="w-4 h-4 mr-1" />
              Application Date
            </label>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value as any)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-red-500 focus:border-red-500 mb-2"
            >
              <option value="all">All Time</option>
              <option value="7days">Last 7 Days</option>
              <option value="30days">Last 30 Days</option>
              <option value="custom">Custom Range</option>
            </select>

            {dateRange === 'custom' && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-red-500 focus:border-red-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">End Date</label>
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-red-500 focus:border-red-500"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Results Count */}
      <div className="pt-4 border-t border-gray-200">
        <p className="text-sm text-gray-600 text-center">
          Showing <span className="font-semibold text-gray-900">{candidatesCount}</span>{' '}
          {candidatesCount === 1 ? 'candidate' : 'candidates'}
        </p>
      </div>
    </div>
  );
};

export default CandidateFilters;
